import { directSupabase } from '../../../lib/supabaseClient';
import type { DocumentLineInput } from '../types/documents.types';
import {
  buildSurveyDeltaSnapshot,
  type SurveyDeltaClassification,
  type SurveyDeltaSnapshot,
} from '../utils/surveyDelta';
import {
  assertFinalSurveyMutable,
  assertFinalSurveyReopenAllowed,
  FINAL_ALREADY_DECIDED,
  FINAL_REVIEW_SUPERSEDED,
  isInFlightFinalReapproval,
  isUnresolvedPendingReviewDelta,
  resolveDecideFinalDeltaAction,
  resolveMarkFinalCompleteAction,
  shouldSupersedeDeltaOnFinalReopen,
} from '../utils/surveyFinalLifecycle';
import { getProjectPaymentSchedule } from '../../../pages/project-workspace/paymentSchedule.service';
import { defaultDepositTerms, isWorkspaceSupplyOnly } from '../../../pages/project-workspace/workspaceSupplyOnly';
import { nullifyDocumentShareLink } from './documentShareLinks.service';
import {
  cloneLineForBoundVersion,
  cloneLineForFinalSurvey,
  isVisualDesignerLine,
} from '../utils/visualDesignerSurveyClone';
import { SURVEY_IMMUTABLE_AFTER_FIRST_TRANSMIT } from '../utils/surveySheetDelete';
import { sheetMatchesVersion } from '../utils/firstSurveyTransmitParity';
import { isMissingRpcError } from '../utils/missingRpc';
import { emitActivityEventBackground } from '../../../services/activityEvents.service';

export type VisualDesignerSurveyKind = 'first' | 'intermediate' | 'final';
export type SurveyCompletionStatus = 'in_progress' | 'complete';

export type FinalSurveyDeltaRecord = {
  id: string;
  projectId: string;
  documentId: string | null;
  firstSurveyId: string;
  finalSurveyId: string;
  firstVersionId: string;
  finalVersionId: string;
  snapshot: SurveyDeltaSnapshot;
  classification: SurveyDeltaClassification;
  completedAt: string;
  completedBy: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  /** Immutable Final snapshot that became the live customer document (No / Final accept). */
  liveVersionId: string | null;
  reapprovalTransmissionVersionId: string | null;
  reapprovalShareLinkId: string | null;
  customerAcceptedAt: string | null;
  customerAcceptedShareLinkId: string | null;
  supersededAt: string | null;
  supersededBy: string | null;
  createdAt: string;
};

function fromDeltaRow(row: any): FinalSurveyDeltaRecord {
  const rawClass = String(row.classification || '').trim();
  const classification: SurveyDeltaClassification =
    rawClass === 'pending_review'
    || rawClass === 'no_reapproval'
    || rawClass === 'customer_reapproval_required'
      ? rawClass
      : null;
  const snapshot = (row.snapshot_json && typeof row.snapshot_json === 'object')
    ? row.snapshot_json as SurveyDeltaSnapshot
    : buildSurveyDeltaSnapshot({
      firstSurveyId: String(row.first_survey_id),
      finalSurveyId: String(row.final_survey_id),
      firstVersionId: String(row.first_version_id),
      finalVersionId: String(row.final_version_id),
      firstHeader: {},
      finalHeader: {},
      firstLines: [],
      finalLines: [],
      classification,
    });
  return {
    id: String(row.id),
    projectId: String(row.project_id),
    documentId: row.document_id ? String(row.document_id) : null,
    firstSurveyId: String(row.first_survey_id),
    finalSurveyId: String(row.final_survey_id),
    firstVersionId: String(row.first_version_id),
    finalVersionId: String(row.final_version_id),
    snapshot: { ...snapshot, classification: snapshot.classification ?? classification },
    classification,
    completedAt: String(row.completed_at || row.created_at || ''),
    completedBy: row.completed_by ? String(row.completed_by) : null,
    decidedAt: row.decided_at ? String(row.decided_at) : null,
    decidedBy: row.decided_by ? String(row.decided_by) : null,
    liveVersionId: row.live_version_id ? String(row.live_version_id) : null,
    reapprovalTransmissionVersionId: row.reapproval_transmission_version_id
      ? String(row.reapproval_transmission_version_id)
      : null,
    reapprovalShareLinkId: row.reapproval_share_link_id ? String(row.reapproval_share_link_id) : null,
    customerAcceptedAt: row.customer_accepted_at ? String(row.customer_accepted_at) : null,
    customerAcceptedShareLinkId: row.customer_accepted_share_link_id
      ? String(row.customer_accepted_share_link_id)
      : null,
    supersededAt: row.superseded_at ? String(row.superseded_at) : null,
    supersededBy: row.superseded_by ? String(row.superseded_by) : null,
    createdAt: String(row.created_at || ''),
  };
}
export type VisualDesignerSurvey = {
  id: string;
  projectId: string;
  optionId: string | null;
  kind: VisualDesignerSurveyKind;
  label: string;
  sortOrder: number;
  clonedFromSurveyId: string | null;
  documentVersionId: string | null;
  completionStatus: SurveyCompletionStatus;
  completedAt: string | null;
  completedBy: string | null;
  reopenedAt: string | null;
  reopenedBy: string | null;
  /** Final only: first edit after provision (Not Started → In Progress). */
  workStartedAt: string | null;
  workStartedBy: string | null;
  createdAt: string;
  updatedAt: string;
};

function fromSurveyRow(row: any): VisualDesignerSurvey {
  const status = String(row.completion_status || 'in_progress').toLowerCase() === 'complete'
    ? 'complete'
    : 'in_progress';
  return {
    id: String(row.id),
    projectId: String(row.project_id),
    optionId: row.option_id ? String(row.option_id) : null,
    kind: row.kind as VisualDesignerSurveyKind,
    label: String(row.label || ''),
    sortOrder: Number(row.sort_order || 0),
    clonedFromSurveyId: row.cloned_from_survey_id ? String(row.cloned_from_survey_id) : null,
    documentVersionId: row.document_version_id ? String(row.document_version_id) : null,
    completionStatus: status,
    completedAt: row.completed_at ? String(row.completed_at) : null,
    completedBy: row.completed_by ? String(row.completed_by) : null,
    reopenedAt: row.reopened_at ? String(row.reopened_at) : null,
    reopenedBy: row.reopened_by ? String(row.reopened_by) : null,
    workStartedAt: row.work_started_at ? String(row.work_started_at) : null,
    workStartedBy: row.work_started_by ? String(row.work_started_by) : null,
    createdAt: String(row.created_at || ''),
    updatedAt: String(row.updated_at || ''),
  };
}

function itemToLine(row: any): DocumentLineInput {
  return {
    lineKind: 'other',
    name: String(row.name || 'Window Design'),
    description: row.description ?? null,
    qty: Number(row.qty || 1),
    unit: row.unit || 'item',
    unitPrice: Number(row.unit_price || 0),
    vatInclusive: !!row.vat_inclusive,
    lineTotal: Number(row.line_total || 0),
    sortOrder: Number(row.sort_order || 0),
    metaJson: row.meta_json ?? null,
  };
}

function lineToItemPayload(surveyId: string, line: DocumentLineInput, idx: number, sourceItemId?: string | null) {
  return {
    survey_id: surveyId,
    source_item_id: sourceItemId || null,
    sort_order: line.sortOrder != null ? line.sortOrder : idx,
    name: line.name,
    description: line.description ?? null,
    qty: line.qty || 1,
    unit: line.unit || 'item',
    unit_price: line.unitPrice || 0,
    vat_inclusive: !!line.vatInclusive,
    line_total: line.lineTotal != null ? line.lineTotal : (line.qty || 1) * (line.unitPrice || 0),
    meta_json: line.metaJson ?? {},
  };
}

/**
 * Refill an empty survey sheet with the Visual Designer lines already saved on its own record.
 * Only inserts into an empty sheet, so a transmitted First Survey regains its designs without edits.
 */
const sheetSeedInFlight = new Map<string, Promise<number>>();

async function seedEmptySheetFromRecord(surveyId: string, versionId: string): Promise<number> {
  if (!surveyId || !versionId) return 0;
  // Concurrent callers would each see an empty sheet and insert the same designs twice.
  const inFlight = sheetSeedInFlight.get(surveyId);
  if (inFlight) return inFlight.then(() => 0);
  const run = seedEmptySheetOnce(surveyId, versionId);
  sheetSeedInFlight.set(surveyId, run);
  try {
    return await run;
  } finally {
    sheetSeedInFlight.delete(surveyId);
  }
}

async function seedEmptySheetOnce(surveyId: string, versionId: string): Promise<number> {
  const { data: seeded, error: rpcErr } = await directSupabase.rpc('seed_survey_items_from_version', {
    p_survey_id: surveyId,
    p_version_id: versionId,
  });
  if (!rpcErr) return Number(seeded || 0);
  if (!isMissingRpcError(String(rpcErr.message || ''))) throw new Error(rpcErr.message);
  return seedEmptySheetClientSide(surveyId, versionId);
}

async function seedEmptySheetClientSide(surveyId: string, versionId: string): Promise<number> {
  const { count, error: countErr } = await directSupabase
    .from('visual_designer_survey_items')
    .select('id', { count: 'exact', head: true })
    .eq('survey_id', surveyId);
  if (countErr) throw new Error(countErr.message);
  if ((count || 0) > 0) return 0;
  const { data: lines, error: linesErr } = await directSupabase
    .from('document_version_lines')
    .select('*')
    .eq('version_id', versionId)
    .order('sort_order', { ascending: true });
  if (linesErr) throw new Error(linesErr.message);
  const vdLines = (lines || []).map(itemToLine).filter(isVisualDesignerLine);
  if (!vdLines.length) return 0;
  const payload = vdLines.map((line, idx) => lineToItemPayload(surveyId, line, idx));
  const { error: insErr } = await directSupabase.from('visual_designer_survey_items').insert(payload);
  if (insErr) throw new Error(insErr.message);
  return vdLines.length;
}

async function getVersionLock(versionId: string): Promise<{ isTransmitted: boolean; overrideUnlocked: boolean }> {
  const { data, error } = await directSupabase
    .from('document_versions')
    .select('is_transmitted, override_unlocked')
    .eq('id', versionId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return {
    isTransmitted: !!data?.is_transmitted,
    overrideUnlocked: !!data?.override_unlocked,
  };
}

async function getDocumentsService() {
  const mod = await import('./documents.service');
  return mod.documentsService;
}

async function findProjectQuotation(projectId: string) {
  const { data: quotation, error: quotationError } = await directSupabase
    .from('documents')
    .select('id, kind, status, active_version_id, accepted_at')
    .eq('project_id', projectId)
    .eq('kind', 'Quotation')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (quotationError) throw new Error(quotationError.message);
  if (quotation) return quotation;

  const { data: estimate, error: estimateError } = await directSupabase
    .from('documents')
    .select('id, kind, status, active_version_id, accepted_at')
    .eq('project_id', projectId)
    .eq('kind', 'Estimate')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (estimateError) throw new Error(estimateError.message);
  return estimate;
}

async function assertProjectFinalSurveyMayChange(projectId?: string | null): Promise<void> {
  const id = String(projectId || '').trim();
  if (!id) return;
  const [{ data: invoiceRows, error: invoiceError }, schedule] = await Promise.all([
    directSupabase
      .from('documents')
      .select('id, invoice_role, status')
      .eq('project_id', id)
      .eq('kind', 'Invoice'),
    getProjectPaymentSchedule(id),
  ]);
  if (invoiceError) throw new Error(invoiceError.message);
  assertFinalSurveyReopenAllowed({
    invoices: (invoiceRows || []).map((row: { id?: string; invoice_role?: string | null; status?: string | null }) => ({
      id: row.id,
      role: row.invoice_role,
      status: row.status,
    })),
    scheduleStages: schedule?.stages || null,
  });
}

/**
 * Legacy Finals can share their record with the First Survey or with something
 * the customer saw. Those must be forked on reopen; a Final that owns its record
 * outright is reopened in place.
 */
async function finalRecordIsReferencedElsewhere(args: {
  versionId: string;
  projectId: string;
}): Promise<boolean> {
  const versionId = String(args.versionId || '').trim();
  if (!versionId) return true;
  const [shareLinks, liveDoc, deltaPins, otherSurveys] = await Promise.all([
    directSupabase
      .from('document_share_links')
      .select('id', { count: 'exact', head: true })
      .eq('sent_version_id', versionId),
    directSupabase
      .from('documents')
      .select('id', { count: 'exact', head: true })
      .eq('customer_live_version_id', versionId),
    directSupabase
      .from('final_survey_deltas')
      .select('id', { count: 'exact', head: true })
      .eq('project_id', args.projectId)
      .or(`reapproval_transmission_version_id.eq.${versionId},live_version_id.eq.${versionId}`),
    directSupabase
      .from('visual_designer_surveys')
      .select('id', { count: 'exact', head: true })
      .eq('document_version_id', versionId)
      .neq('kind', 'final'),
  ]);
  for (const result of [shareLinks, liveDoc, deltaPins, otherSurveys]) {
    if (result.error) return true;
    if ((result.count || 0) > 0) return true;
  }
  return false;
}

async function setDocumentCustomerLiveVersion(documentId: string, liveVersionId: string): Promise<void> {
  const docId = String(documentId || '').trim();
  const versionId = String(liveVersionId || '').trim();
  if (!docId || !versionId) return;
  const { error } = await directSupabase
    .from('documents')
    .update({ customer_live_version_id: versionId })
    .eq('id', docId);
  if (error) throw new Error(error.message);
}

/**
 * Resolve the immutable First Survey transmission the customer accepted (share-pinned),
 * falling back to the First Survey working bind when no share pin exists (legacy).
 */
async function resolveAcceptedFirstTransmissionVersionId(args: {
  projectId: string;
  firstSurveyId: string;
  firstWorkingVersionId: string | null;
  finalWorkingVersionId?: string | null;
}): Promise<string> {
  const quote = await findProjectQuotation(args.projectId);
  if (!quote?.id) {
    throw new Error('Workspace quote not found; cannot resolve accepted First Survey transmission.');
  }

  const { data: links, error: linksErr } = await directSupabase
    .from('document_share_links')
    .select('id, sent_version_id, last_sent_at, created_at, revoked_at')
    .eq('document_id', quote.id)
    .not('sent_version_id', 'is', null)
    .order('last_sent_at', { ascending: false });
  if (linksErr) throw new Error(linksErr.message);

  const candidates = (links || [])
    .map((row: any) => ({
      id: String(row.id),
      sentVersionId: String(row.sent_version_id || '').trim(),
      sentAt: String(row.last_sent_at || row.created_at || ''),
      revoked: !!row.revoked_at,
    }))
    .filter((row) => row.sentVersionId && !row.revoked);

  const finalWorking = String(args.finalWorkingVersionId || '').trim();
  const firstWorking = String(args.firstWorkingVersionId || '').trim();

  // Prefer share-pinned transmissions that are not the Final working bind.
  const preferred = candidates.find((row) => row.sentVersionId !== finalWorking);
  if (preferred?.sentVersionId) return preferred.sentVersionId;

  if (firstWorking) return firstWorking;
  throw new Error('Accepted First Survey transmission not found. Transmit First Survey before completing Final.');
}

export function isSurveyWritable(
  survey: VisualDesignerSurvey | null,
  versionLock?: { isTransmitted: boolean; overrideUnlocked: boolean } | null,
  options?: { hasFinalSurvey?: boolean; firstIsTransmitted?: boolean; unresolvedPendingReview?: boolean },
): boolean {
  if (!survey) return false;
  // First is permanently immutable after first transmission. Final existing is not the lock.
  if (survey.kind === 'first' && (options?.firstIsTransmitted || versionLock?.isTransmitted)) return false;
  // Complete / pending-review Final is frozen until Reopen or Yes/No — lock flags cannot unfreeze it.
  if (survey.kind === 'final' && survey.completionStatus === 'complete') return false;
  if (survey.kind === 'final' && options?.unresolvedPendingReview) return false;
  // Incomplete Final stays freely editable — no Unlock while working.
  if (survey.kind === 'final' && survey.completionStatus !== 'complete') return true;
  // Complete Final: only writable after Reopen (new working cycle / unlocked fork).
  if (!survey.documentVersionId) {
    if (survey.kind === 'final' && survey.completionStatus === 'complete') return false;
    return true;
  }
  if (!versionLock) return true;
  if (versionLock.isTransmitted && !versionLock.overrideUnlocked) return false;
  return true;
}

async function projectHasFinalSurvey(projectId: string, exceptSurveyId?: string | null): Promise<boolean> {
  const id = String(projectId || '').trim();
  if (!id) return false;
  let query = directSupabase
    .from('visual_designer_surveys')
    .select('id')
    .eq('project_id', id)
    .eq('kind', 'final')
    .limit(1);
  const except = String(exceptSurveyId || '').trim();
  if (except) query = query.neq('id', except);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data || []).length > 0;
}

/**
 * True once First Survey has been transmitted for this workspace — including orphans
 * where First/Final rows were reset but share links or transmitted versions remain.
 */
async function projectFirstSurveyIsImmutable(projectId: string): Promise<boolean> {
  const id = String(projectId || '').trim();
  if (!id) return false;
  if (await projectHasFinalSurvey(id)) return true;

  const quote = await findProjectQuotation(id);
  if (!quote?.id) return false;

  const { data: versions, error: verErr } = await directSupabase
    .from('document_versions')
    .select('id, is_transmitted')
    .eq('document_id', quote.id);
  if (verErr) throw new Error(verErr.message);
  if ((versions || []).some((row: any) => !!row.is_transmitted)) return true;

  const { data: links, error: linkErr } = await directSupabase
    .from('document_share_links')
    .select('id')
    .eq('document_id', quote.id)
    .eq('survey_artefact', 'first')
    .limit(1);
  if (linkErr) {
    if (/survey_artefact|schema cache/i.test(String(linkErr.message || ''))) return false;
    throw new Error(linkErr.message);
  }
  return (links || []).length > 0;
}

async function assertFirstSurveyMutable(survey: VisualDesignerSurvey): Promise<void> {
  if (survey.kind !== 'first') return;
  if (await projectFirstSurveyIsImmutable(survey.projectId)) {
    throw new Error(
      'First Survey is permanently immutable after transmission. Edit Final Survey instead.',
    );
  }
}

async function fetchCurrentFinalSurveyDelta(finalSurveyId: string): Promise<FinalSurveyDeltaRecord | null> {
  const { data, error } = await directSupabase
    .from('final_survey_deltas')
    .select('*')
    .eq('final_survey_id', finalSurveyId)
    .is('superseded_at', null)
    .order('completed_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    if (/superseded_at/i.test(error.message)) {
      const fallback = await directSupabase
        .from('final_survey_deltas')
        .select('*')
        .eq('final_survey_id', finalSurveyId)
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (fallback.error) throw new Error(fallback.error.message);
      if (!fallback.data) return null;
      return fromDeltaRow(fallback.data);
    }
    throw new Error(error.message);
  }
  if (!data) return null;
  return fromDeltaRow(data);
}

async function assertFinalSurveyEditable(survey: VisualDesignerSurvey): Promise<void> {
  if (survey.kind !== 'final') return;
  const currentDelta = await fetchCurrentFinalSurveyDelta(survey.id);
  assertFinalSurveyMutable({
    kind: survey.kind,
    completionStatus: survey.completionStatus,
    documentVersionId: survey.documentVersionId,
    currentDelta,
  });
}

async function markFinalWorkStartedIfNeeded(survey: VisualDesignerSurvey): Promise<void> {
  if (survey.kind !== 'final') return;
  if (survey.completionStatus === 'complete') return;
  if (survey.workStartedAt) return;
  const { data: userRes } = await directSupabase.auth.getUser();
  const userId = userRes.user?.id ?? null;
  await directSupabase
    .from('visual_designer_surveys')
    .update({
      work_started_at: new Date().toISOString(),
      work_started_by: userId,
    })
    .eq('id', survey.id)
    .is('work_started_at', null);
}

const finalProvisionQueue = new Map<string, Promise<unknown>>();

export const visualDesignerSurveysService = {
  /**
   * True when this workspace already has a Final Survey (lifecycle handover from First).
   */
  async projectHasFinalSurvey(projectId: string): Promise<boolean> {
    return projectHasFinalSurvey(projectId);
  },

  async projectFirstSurveyIsImmutable(projectId: string): Promise<boolean> {
    return projectFirstSurveyIsImmutable(projectId);
  },

  async listByProject(projectId: string): Promise<VisualDesignerSurvey[]> {
    const { data, error } = await directSupabase
      .from('visual_designer_surveys')
      .select('*')
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true });
    if (error) throw new Error(error.message);
    return (data || []).map(fromSurveyRow);
  },

  async ensureFirstSurvey(projectId: string): Promise<VisualDesignerSurvey> {
    const existing = await this.listByProject(projectId);
    const first = existing.find((s) => s.kind === 'first' && !s.optionId)
      || existing.find((s) => s.kind === 'first');
    if (first) return first;
    const { data, error } = await directSupabase
      .from('visual_designer_surveys')
      .insert({
        project_id: projectId,
        kind: 'first',
        label: 'First Survey',
        sort_order: 0,
      })
      .select('*')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error('Failed to create First Survey');
    return fromSurveyRow(data);
  },

  /** Ensure First Survey is bound to the workspace quote's active version. */
  async ensureFirstSurveyBoundToQuote(projectId: string, documentId?: string | null): Promise<VisualDesignerSurvey> {
    const first = await this.ensureFirstSurvey(projectId);
    const docs = await getDocumentsService();
    const quoteId = documentId || (await findProjectQuotation(projectId))?.id;
    if (!quoteId) return first;

    const active = await docs.getActiveVersion(String(quoteId));
    const versionId = active?.header?.id ? String(active.header.id) : null;
    if (!versionId) return first;

    const boundVersionId = first.documentVersionId ? String(first.documentVersionId) : '';
    if (boundVersionId && boundVersionId === versionId) return first;

    // Keep a valid in-quote binding (e.g. older working version on the same document).
    let isOrphan = !boundVersionId;
    if (boundVersionId && boundVersionId !== versionId) {
      const bound = await docs.getVersion(boundVersionId).catch(() => null);
      const boundDocId = bound?.header?.document_id != null ? String(bound.header.document_id) : '';
      if (boundDocId && boundDocId === String(quoteId)) return first;
      isOrphan = true;
    }

    if (!isOrphan) return first;

    // The quote's active version already belongs to another survey (seen on jobs that started
    // on a legacy invoice). Binding would always fail, and clearing First's sheet below would
    // lose it, so leave both as they are; Final Survey provisioning clones the active version.
    const { data: activePairing } = await directSupabase
      .from('document_versions')
      .select('visual_survey_id')
      .eq('id', versionId)
      .maybeSingle();
    const pairedSurveyId = activePairing?.visual_survey_id ? String(activePairing.visual_survey_id) : '';
    if (pairedSurveyId && pairedSurveyId !== first.id) {
      console.warn('Quote version already paired with another survey; leaving First Survey binding unchanged.', {
        projectId,
        versionId,
        pairedSurveyId,
        firstSurveyId: first.id,
      });
      return first;
    }

    // Transmitted First stays on the estimate until accept-promotion rebinds it.
    // Never wipe that historical sheet just because a quote now exists.
    if (boundVersionId) {
      const boundLock = await getVersionLock(boundVersionId).catch(() => null);
      if (boundLock?.isTransmitted) return first;
      if (await projectFirstSurveyIsImmutable(projectId)) return first;
    }

    // Cross-document / missing-version orphans: clear ghost bind + items so we never
    // rehydrate transmitted lock state or old VD content onto a live quote.
    if (boundVersionId) {
      await directSupabase
        .from('document_versions')
        .update({ visual_survey_id: null })
        .eq('id', boundVersionId)
        .eq('visual_survey_id', first.id);
      await directSupabase
        .from('visual_designer_surveys')
        .update({ document_version_id: null })
        .eq('id', first.id);
      const { error: clearItemsErr } = await directSupabase
        .from('visual_designer_survey_items')
        .delete()
        .eq('survey_id', first.id);
      if (clearItemsErr) throw new Error(clearItemsErr.message);
      await this.bindToVersion(first.id, versionId);
      await seedEmptySheetFromRecord(first.id, versionId);
      return this.getById(first.id);
    }

    // Simply unbound: bind, then either project survey→version or seed survey←version.
    await this.bindToVersion(first.id, versionId);
    const surveyItems = await this.listItems(first.id);
    if (surveyItems.length > 0) {
      await this.projectToVersion(first.id, versionId);
    } else {
      await seedEmptySheetFromRecord(first.id, versionId);
    }
    return this.getById(first.id);
  },

  async listItems(surveyId: string): Promise<DocumentLineInput[]> {
    const { data, error } = await directSupabase
      .from('visual_designer_survey_items')
      .select('*')
      .eq('survey_id', surveyId)
      .order('sort_order', { ascending: true });
    if (error) throw new Error(error.message);
    return (data || []).map(itemToLine);
  },

  async replaceItems(surveyId: string, lines: DocumentLineInput[]): Promise<void> {
    const survey = await this.getById(surveyId);
    await assertFirstSurveyMutable(survey);
    await assertFinalSurveyEditable(survey);
    if (survey.documentVersionId) {
      const lock = await getVersionLock(survey.documentVersionId);
      const hasFinal = survey.kind === 'first'
        ? await projectHasFinalSurvey(survey.projectId)
        : false;
      if (!isSurveyWritable(survey, lock, {
        hasFinalSurvey: hasFinal,
        firstIsTransmitted: survey.kind === 'first' ? lock.isTransmitted : false,
      })) {
        throw new Error(
          survey.kind === 'first'
            ? 'First Survey is permanently immutable after transmission. Edit Final Survey instead.'
            : 'This survey is locked. Reopen Final Survey before editing.',
        );
      }
    } else if (survey.kind === 'final' && survey.completionStatus === 'complete') {
      throw new Error('Final Survey is complete. Reopen Final Survey before editing.');
    }
    await markFinalWorkStartedIfNeeded(survey);
    const vdLines = (lines || []).filter(isVisualDesignerLine);
    const payload = vdLines.map((line, idx) => lineToItemPayload(surveyId, line, idx));
    const { error: rpcErr } = await directSupabase.rpc('replace_survey_items', {
      p_survey_id: surveyId,
      p_items: payload,
    });
    if (rpcErr) {
      if (!isMissingRpcError(String(rpcErr.message || ''))) throw new Error(rpcErr.message);
      const { error: delErr } = await directSupabase
        .from('visual_designer_survey_items')
        .delete()
        .eq('survey_id', surveyId);
      if (delErr) throw new Error(delErr.message);
      if (payload.length === 0) return;
      const { error: insErr } = await directSupabase.from('visual_designer_survey_items').insert(payload);
      if (insErr) throw new Error(insErr.message);
    }
    if (payload.length === 0) return;
    if (survey.documentVersionId) {
      const docs = await getDocumentsService();
      await docs.markVersionDivergedFromClone(survey.documentVersionId).catch(() => {
        // Divergence tracking must never block survey autosave.
      });
    }
  },

  async getById(surveyId: string): Promise<VisualDesignerSurvey> {
    const { data, error } = await directSupabase
      .from('visual_designer_surveys')
      .select('*')
      .eq('id', surveyId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error('Survey not found');
    return fromSurveyRow(data);
  },

  /**
   * Provision Final Survey as an exact working clone of the transmitted First Survey.
   * Primary trigger: successful First Survey customer transmission (not accept/cash).
   * Idempotent if Final already exists.
   *
   * Optional `fromTransmissionVersionId` clones from the immutable customer snapshot
   * so Final matches exactly what was sent.
   *
   * Calls for the same project run one at a time: each reads state before it
   * writes, so overlapping calls would each fork a record and orphan all but one.
   */
  async createFinalSurveyFromFirst(
    projectId: string,
    options?: { fromTransmissionVersionId?: string | null },
  ): Promise<VisualDesignerSurvey> {
    const key = String(projectId || '').trim();
    const previous = finalProvisionQueue.get(key) || Promise.resolve();
    const run = previous
      .catch(() => undefined)
      .then(() => this.provisionFinalSurveyFromFirst(projectId, options));
    const settled = run.catch(() => undefined);
    finalProvisionQueue.set(key, settled);
    void settled.then(() => {
      if (finalProvisionQueue.get(key) === settled) finalProvisionQueue.delete(key);
    });
    return run;
  },

  async provisionFinalSurveyFromFirst(
    projectId: string,
    options?: { fromTransmissionVersionId?: string | null },
  ): Promise<VisualDesignerSurvey> {
    const surveys = await this.listByProject(projectId);
    const existingFinal = surveys.find((s) => s.kind === 'final' && !s.optionId)
      || surveys.find((s) => s.kind === 'final');

    const docs = await getDocumentsService();
    const quote = await findProjectQuotation(projectId);
    if (!quote?.id || String(quote.kind) !== 'Quotation') {
      throw new Error('Final Survey belongs on a quote. Accept the estimate first.');
    }

    let first = surveys.find((s) => s.kind === 'first' && !s.optionId)
      || surveys.find((s) => s.kind === 'first')
      || await this.ensureFirstSurvey(projectId);
    first = await this.ensureFirstSurveyBoundToQuote(projectId, quote.id);

    // Prefer First Survey's bound version; otherwise use/create the quote's active version.
    let firstVersionId = first.documentVersionId ? String(first.documentVersionId) : '';
    if (firstVersionId) {
      // A First still bound to another document (e.g. a removed legacy invoice) cannot be the
      // baseline for this quote; clone the quote's active version instead.
      const bound = await docs.getVersion(firstVersionId).catch(() => null);
      const boundDocId = bound?.header?.document_id != null ? String(bound.header.document_id) : '';
      if (boundDocId !== String(quote.id)) {
        const active = await docs.getActiveVersion(String(quote.id));
        firstVersionId = active?.header?.id ? String(active.header.id) : '';
      }
    }
    if (!firstVersionId) {
      const active = await docs.getActiveVersion(String(quote.id));
      firstVersionId = active?.header?.id ? String(active.header.id) : '';
      if (firstVersionId && !first.documentVersionId) {
        try {
          await this.bindToVersion(first.id, firstVersionId);
          await this.projectToVersion(first.id, firstVersionId);
          first = await this.getById(first.id);
        } catch (err) {
          console.warn('Could not bind First Survey before Final Survey provision:', err);
        }
      }
    }
    if (!firstVersionId) {
      throw new Error('First Survey has no quote version to clone. Save Quote Details on First Survey first.');
    }

    const transmissionId = String(options?.fromTransmissionVersionId || '').trim();
    const cloneSourceVersionId = transmissionId || firstVersionId;

    // Existing Final with its own version: keep it, but repair virgin Final if it still shares
    // First's transmitted working copy or was wrongly marked transmitted before Mark Complete.
    // Never unfreeze a completed Final or one with an unresolved pending review.
    if (existingFinal?.documentVersionId) {
      const currentDelta = await fetchCurrentFinalSurveyDelta(existingFinal.id);
      const frozen = existingFinal.completionStatus === 'complete'
        || isUnresolvedPendingReviewDelta(currentDelta);
      const finalVersionId = String(existingFinal.documentVersionId);
      const sharesFirstWorking = finalVersionId === String(firstVersionId)
        || (transmissionId && finalVersionId === transmissionId);
      if (sharesFirstWorking && !frozen) {
        try {
          const clonedVersion = await docs.cloneVersionFrom(String(quote.id), cloneSourceVersionId);
          const nextId = String(clonedVersion.id);
          await directSupabase
            .from('document_versions')
            .update({
              is_transmitted: false,
              override_unlocked: false,
              transmitted_at: null,
              transmitted_by: null,
            })
            .eq('id', nextId);
          await this.rebindToVersion(existingFinal.id, nextId);
          await this.projectToVersion(existingFinal.id, nextId);
          await docs.activateVersion(String(quote.id), nextId);
        } catch (err) {
          console.warn('Failed to fork writable Final Survey version from First:', err);
        }
        return this.getById(existingFinal.id);
      }
      if (!frozen) {
        await directSupabase
          .from('document_versions')
          .update({
            is_transmitted: false,
            override_unlocked: false,
            transmitted_at: null,
            transmitted_by: null,
          })
          .eq('id', finalVersionId)
          .eq('is_transmitted', true);
      }
      return this.getById(existingFinal.id);
    }

    // Create the Final Survey row first so the workspace tab becomes live even if versioning fails later.
    let created = existingFinal;
    if (!created) {
      await seedEmptySheetFromRecord(first.id, firstVersionId);
      const firstItems = await this.listItems(first.id);
      const nextOrder = surveys.reduce((max, s) => Math.max(max, s.sortOrder), 0) + 1;
      const { data, error } = await directSupabase
        .from('visual_designer_surveys')
        .insert({
          project_id: projectId,
          kind: 'final',
          label: 'Final Survey',
          sort_order: nextOrder,
          cloned_from_survey_id: first.id,
          completion_status: 'in_progress',
          work_started_at: null,
          work_started_by: null,
        })
        .select('*')
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) throw new Error('Failed to create Final Survey');
      created = fromSurveyRow(data);

      const clonedItems = firstItems.filter(isVisualDesignerLine).map(cloneLineForFinalSurvey);
      if (clonedItems.length > 0) {
        const payload = clonedItems.map((line, idx) => lineToItemPayload(created!.id, line, idx));
        const { error: insErr } = await directSupabase.from('visual_designer_survey_items').insert(payload);
        if (insErr) throw new Error(insErr.message);
      }
      emitActivityEventBackground({
        action: 'survey.final_created',
        entityType: 'survey',
        entityId: created.id,
        projectId,
        label: 'Final survey created by system',
        metadata: {
          clonedFromSurveyId: first.id,
          fromTransmissionVersionId: transmissionId || null,
        },
      });
    }

    // Preserve transmitted First Survey as immutable historical baseline.
    try {
      await docs.markVersionTransmitted(firstVersionId);
    } catch (err) {
      console.warn('Could not mark First Survey version transmitted:', err);
    }

    try {
      const clonedVersion = await docs.cloneVersionFrom(String(quote.id), cloneSourceVersionId);
      const finalVersionId = String(clonedVersion.id);
      // Virgin Final working copy must stay editable until Mark Complete.
      await directSupabase
        .from('document_versions')
        .update({
          is_transmitted: false,
          override_unlocked: false,
          transmitted_at: null,
          transmitted_by: null,
        })
        .eq('id', finalVersionId);
      await this.bindToVersion(created.id, finalVersionId);
      await this.projectToVersion(created.id, finalVersionId);
      await docs.activateVersion(String(quote.id), finalVersionId);
    } catch (err) {
      console.warn('Final Survey version clone/bind deferred:', err);
      return this.getById(created.id);
    }

    return this.getById(created.id);
  },

  /**
   * Finals provisioned before the First sheet held its designs start empty and are never re-cloned.
   * Copy the accepted First Survey's designs into an in-progress Final that still has none.
   */
  async copyFirstDesignsIntoEmptyFinal(projectId: string): Promise<boolean> {
    const key = String(projectId || '').trim();
    const previous = finalProvisionQueue.get(key) || Promise.resolve();
    const run = previous
      .catch(() => undefined)
      .then(() => this.copyFirstDesignsIntoEmptyFinalNow(projectId));
    const settled = run.catch(() => undefined);
    finalProvisionQueue.set(key, settled);
    void settled.then(() => {
      if (finalProvisionQueue.get(key) === settled) finalProvisionQueue.delete(key);
    });
    return run;
  },

  async copyFirstDesignsIntoEmptyFinalNow(projectId: string): Promise<boolean> {
    const surveys = await this.listByProject(projectId);
    const final = surveys.find((s) => s.kind === 'final' && !s.optionId);
    const first = surveys.find((s) => s.kind === 'first' && !s.optionId);
    if (!final?.documentVersionId || !first?.documentVersionId) return false;
    if (final.completionStatus === 'complete') return false;
    if (isUnresolvedPendingReviewDelta(await fetchCurrentFinalSurveyDelta(final.id))) return false;

    const quote = await findProjectQuotation(projectId);
    const quoteStatus = String(quote?.status || '').trim().toLowerCase();
    if (String(quote?.kind) !== 'Quotation' || (quoteStatus !== 'accepted' && quoteStatus !== 'cash')) return false;

    const finalItems = await this.listItems(final.id);
    if (finalItems.length > 0) return false;

    await seedEmptySheetFromRecord(first.id, String(first.documentVersionId));
    const clonedItems = (await this.listItems(first.id))
      .filter(isVisualDesignerLine)
      .map(cloneLineForFinalSurvey);
    if (clonedItems.length === 0) return false;

    const payload = clonedItems.map((line, idx) => lineToItemPayload(final.id, line, idx));
    const { error: insErr } = await directSupabase.from('visual_designer_survey_items').insert(payload);
    if (insErr) throw new Error(insErr.message);
    await this.projectToVersion(final.id, String(final.documentVersionId));

    emitActivityEventBackground({
      action: 'survey.final_designs_copied',
      entityType: 'survey',
      entityId: final.id,
      projectId,
      label: 'First Survey designs copied into empty Final Survey',
      metadata: { firstSurveyId: first.id, designs: clonedItems.length },
    });
    return true;
  },

  async createIntermediateFrom(projectId: string, sourceSurveyId: string, label: string): Promise<VisualDesignerSurvey> {
    const surveys = await this.listByProject(projectId);
    const source = surveys.find((s) => s.id === sourceSurveyId)
      || surveys.find((s) => s.kind === 'first')
      || surveys[0]
      || await this.ensureFirstSurvey(projectId);
    const items = await this.listItems(source.id);
    const nextOrder = surveys.reduce((max, s) => Math.max(max, s.sortOrder), 0) + 1;
    const { data, error } = await directSupabase
      .from('visual_designer_surveys')
      .insert({
        project_id: projectId,
        kind: 'intermediate',
        label: label.trim() || 'Intermediary Stage',
        sort_order: nextOrder,
        cloned_from_survey_id: source.id,
      })
      .select('*')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error('Failed to create survey stage');
    const created = fromSurveyRow(data);
    const clonedItems = items.filter(isVisualDesignerLine).map(cloneLineForBoundVersion);
    if (clonedItems.length > 0) {
      const payload = clonedItems.map((line, idx) => lineToItemPayload(created.id, line, idx));
      const { error: insErr } = await directSupabase.from('visual_designer_survey_items').insert(payload);
      if (insErr) throw new Error(insErr.message);
    }
    return created;
  },

  async bindToVersion(surveyId: string, versionId: string): Promise<void> {
    const survey = await this.getById(surveyId);
    if (survey.documentVersionId && survey.documentVersionId !== versionId) {
      throw new Error('This survey is already paired with another quote version. Unlock override to edit, or create a new survey to reissue.');
    }
    const { data: version, error: vErr } = await directSupabase
      .from('document_versions')
      .select('id, visual_survey_id, is_transmitted, override_unlocked')
      .eq('id', versionId)
      .maybeSingle();
    if (vErr) throw new Error(vErr.message);
    if (!version) throw new Error('Document version not found');
    if (version.is_transmitted && !version.override_unlocked && version.visual_survey_id && String(version.visual_survey_id) !== surveyId) {
      throw new Error('This transmitted version is locked. Unlock override for a small change, or reissue a new quote version.');
    }
    if (version.visual_survey_id && String(version.visual_survey_id) !== surveyId) {
      throw new Error('This quote version already has a Visual Designer pairing.');
    }
    const { error: sErr } = await directSupabase
      .from('visual_designer_surveys')
      .update({ document_version_id: versionId })
      .eq('id', surveyId);
    if (sErr) throw new Error(sErr.message);
    const { error: dErr } = await directSupabase
      .from('document_versions')
      .update({ visual_survey_id: surveyId })
      .eq('id', versionId);
    if (dErr) throw new Error(dErr.message);
  },

  /** Move survey pointer to a new version; clears the old version bind for history retention. */
  async rebindToVersion(surveyId: string, newVersionId: string): Promise<void> {
    const survey = await this.getById(surveyId);
    const oldVersionId = survey.documentVersionId;
    if (oldVersionId && oldVersionId === newVersionId) return;
    if (oldVersionId) {
      await directSupabase
        .from('document_versions')
        .update({ visual_survey_id: null })
        .eq('id', oldVersionId)
        .eq('visual_survey_id', surveyId);
      await directSupabase
        .from('visual_designer_surveys')
        .update({ document_version_id: null })
        .eq('id', surveyId);
    }
    await this.bindToVersion(surveyId, newVersionId);
  },

  async projectToVersion(surveyId: string, versionId: string): Promise<void> {
    const survey = await this.getById(surveyId);
    await assertFirstSurveyMutable(survey);
    const lock = await getVersionLock(versionId);
    // Virgin Final may project while in progress even if a stale transmitted flag remains.
    const virginFinal = survey.kind === 'final' && survey.completionStatus !== 'complete';
    if (lock.isTransmitted && !lock.overrideUnlocked && !virginFinal) {
      throw new Error('This transmitted version is locked. Unlock override before editing.');
    }
    const { error: rpcErr } = await directSupabase.rpc('project_survey_to_version', {
      p_survey_id: surveyId,
      p_version_id: versionId,
    });
    if (!rpcErr) return;
    if (!isMissingRpcError(String(rpcErr.message || ''))) throw new Error(rpcErr.message);
    const vdLines = await this.listItems(surveyId);
    const { data: existing, error: eErr } = await directSupabase
      .from('document_version_lines')
      .select('*')
      .eq('version_id', versionId)
      .order('sort_order', { ascending: true });
    if (eErr) throw new Error(eErr.message);
    const commercial = (existing || []).filter((row: any) => String(row?.meta_json?.feature || '').trim() !== 'visual_designer');
    const { error: delErr } = await directSupabase.from('document_version_lines').delete().eq('version_id', versionId);
    if (delErr) throw new Error(delErr.message);
    const vdPayload = vdLines.map((line, idx) => ({
      version_id: versionId,
      line_kind: 'other',
      additional_type: null,
      material_id: null,
      equipment_id: null,
      labour_rate_id: null,
      name: line.name,
      description: line.description ?? null,
      qty: line.qty || 1,
      unit: line.unit || 'item',
      unit_price: line.unitPrice || 0,
      vat_inclusive: !!line.vatInclusive,
      line_total: line.lineTotal != null ? line.lineTotal : (line.qty || 1) * (line.unitPrice || 0),
      sort_order: idx,
      labour_days: null,
      labour_hours: null,
      meta_json: line.metaJson ?? null,
    }));
    const commercialPayload = commercial.map((row: any, idx: number) => ({
      version_id: versionId,
      line_kind: row.line_kind,
      additional_type: row.additional_type ?? null,
      material_id: row.material_id || null,
      equipment_id: row.equipment_id || null,
      labour_rate_id: row.labour_rate_id || null,
      name: row.name,
      description: row.description,
      qty: row.qty,
      unit: row.unit,
      unit_price: row.unit_price,
      vat_inclusive: row.vat_inclusive,
      line_total: row.line_total,
      sort_order: vdPayload.length + idx,
      labour_days: row.labour_days,
      labour_hours: row.labour_hours,
      meta_json: row.meta_json ?? null,
    }));
    const payload = [...vdPayload, ...commercialPayload];
    if (payload.length === 0) return;
    const { error: insErr } = await directSupabase.from('document_version_lines').insert(payload);
    if (insErr) throw new Error(insErr.message);
  },

  /**
   * Before a First Survey send: the version's designs must be an exact copy of the sheet,
   * because the customer snapshot is cloned from the version.
   */
  async alignFirstSheetForTransmit(projectId: string, versionId: string): Promise<void> {
    const surveys = await this.listByProject(projectId);
    const first = surveys.find((s) => s.kind === 'first' && s.documentVersionId === versionId);
    if (!first) return;

    const loadVersionDesigns = async () => {
      const { data, error } = await directSupabase
        .from('document_version_lines')
        .select('name, qty, meta_json')
        .eq('version_id', versionId)
        .eq('meta_json->>feature', 'visual_designer');
      if (error) throw new Error(error.message);
      return data || [];
    };

    const sheet = (await this.listItems(first.id)).filter(isVisualDesignerLine);
    if (sheet.length === 0) {
      await seedEmptySheetFromRecord(first.id, versionId);
      return;
    }
    if (sheetMatchesVersion(sheet, await loadVersionDesigns())) return;

    await this.projectToVersion(first.id, versionId);
    if (!sheetMatchesVersion(sheet, await loadVersionDesigns())) {
      throw new Error('The First Survey designs and the quote do not match, so nothing was sent. Refresh the page and try again.');
    }
  },

  async replaceItemsAndProject(surveyId: string, lines: DocumentLineInput[]): Promise<void> {
    await this.replaceItems(surveyId, lines);
    const survey = await this.getById(surveyId);
    if (survey.documentVersionId) {
      await this.projectToVersion(surveyId, survey.documentVersionId);
    }
  },

  async getLatestFinalSurveyDelta(finalSurveyId: string): Promise<FinalSurveyDeltaRecord | null> {
    return this.getCurrentFinalSurveyDelta(finalSurveyId);
  },

  async getCurrentFinalSurveyDelta(finalSurveyId: string): Promise<FinalSurveyDeltaRecord | null> {
    return fetchCurrentFinalSurveyDelta(finalSurveyId);
  },

  async getFinalSurveyDeltaById(deltaId: string): Promise<FinalSurveyDeltaRecord> {
    const { data, error } = await directSupabase
      .from('final_survey_deltas')
      .select('*')
      .eq('id', deltaId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error('Final Survey delta not found.');
    return fromDeltaRow(data);
  },

  /** All Final reapproval transmission / share-link pins for classification. */
  async listFinalReapprovalPins(finalSurveyId: string): Promise<{
    transmissionVersionIds: string[];
    shareLinkIds: string[];
  }> {
    const id = String(finalSurveyId || '').trim();
    if (!id) return { transmissionVersionIds: [], shareLinkIds: [] };
    const { data, error } = await directSupabase
      .from('final_survey_deltas')
      .select('reapproval_transmission_version_id, reapproval_share_link_id')
      .eq('final_survey_id', id);
    if (error) throw new Error(error.message);
    const transmissionVersionIds: string[] = [];
    const shareLinkIds: string[] = [];
    (data || []).forEach((row: any) => {
      const tx = String(row.reapproval_transmission_version_id || '').trim();
      const share = String(row.reapproval_share_link_id || '').trim();
      if (tx) transmissionVersionIds.push(tx);
      if (share) shareLinkIds.push(share);
    });
    return {
      transmissionVersionIds: [...new Set(transmissionVersionIds)],
      shareLinkIds: [...new Set(shareLinkIds)],
    };
  },

  /**
   * Mark Final Survey complete:
   * Persisted lifecycle transition — freeze immediately, store First→Final delta as pending_review.
   * Closing Review Changes later does not undo this. Always compares against the original First transmission.
   */
  async markFinalSurveyComplete(surveyId: string): Promise<{ survey: VisualDesignerSurvey; delta: FinalSurveyDeltaRecord }> {
    const survey = await this.getById(surveyId);
    if (survey.kind !== 'final') throw new Error('Only Final Survey can be marked complete.');
    if (!survey.documentVersionId) {
      throw new Error('Final Survey has no quote version. Save Quote Details before marking complete.');
    }

    const currentDelta = await fetchCurrentFinalSurveyDelta(survey.id);
    const action = resolveMarkFinalCompleteAction({
      kind: survey.kind,
      completionStatus: survey.completionStatus,
      documentVersionId: survey.documentVersionId,
      currentDelta,
    });
    if (action.type === 'reject') throw new Error(action.reason);
    if (action.type === 'return_existing_pending') {
      const existing = currentDelta && currentDelta.id === action.deltaId
        ? currentDelta
        : await this.getFinalSurveyDeltaById(action.deltaId);
      if (survey.completionStatus !== 'complete') {
        const { data: userRes } = await directSupabase.auth.getUser();
        const userId = userRes.user?.id ?? null;
        const { data: repaired, error: repairErr } = await directSupabase
          .from('visual_designer_surveys')
          .update({
            completion_status: 'complete',
            completed_at: existing.completedAt || new Date().toISOString(),
            completed_by: existing.completedBy || userId,
          })
          .eq('id', survey.id)
          .select('*')
          .maybeSingle();
        if (repairErr) throw new Error(repairErr.message);
        const docs = await getDocumentsService();
        await docs.markVersionTransmitted(survey.documentVersionId).catch(() => undefined);
        return { survey: repaired ? fromSurveyRow(repaired) : survey, delta: existing };
      }
      return { survey, delta: existing };
    }

    const docs = await getDocumentsService();
    const first = (await this.listByProject(survey.projectId)).find((s) => s.kind === 'first');
    if (!first) {
      throw new Error('First Survey is missing; cannot compute delta.');
    }

    const acceptedFirstVersionId = await resolveAcceptedFirstTransmissionVersionId({
      projectId: survey.projectId,
      firstSurveyId: first.id,
      firstWorkingVersionId: first.documentVersionId,
      finalWorkingVersionId: survey.documentVersionId,
    });
    const firstLoaded = await docs.getVersion(acceptedFirstVersionId);
    if (!firstLoaded?.header) {
      throw new Error('Could not load First transmission / Final working records for delta.');
    }
    const finalLoaded = await docs.getVersion(survey.documentVersionId);
    if (!finalLoaded?.header) {
      throw new Error('Could not load First transmission / Final working records for delta.');
    }

    const { data: userRes } = await directSupabase.auth.getUser();
    const userId = userRes.user?.id ?? null;
    const nowIso = new Date().toISOString();

    // Persist completion first so repair/unfreeze cannot race the freeze.
    const { data: updated, error: upErr } = await directSupabase
      .from('visual_designer_surveys')
      .update({
        completion_status: 'complete',
        completed_at: nowIso,
        completed_by: userId,
      })
      .eq('id', survey.id)
      .eq('completion_status', 'in_progress')
      .select('*')
      .maybeSingle();
    if (upErr) throw new Error(upErr.message);
    if (!updated) {
      const latest = await this.getById(survey.id);
      const existing = await fetchCurrentFinalSurveyDelta(survey.id);
      if (existing && isUnresolvedPendingReviewDelta(existing)) {
        return { survey: latest, delta: existing };
      }
      throw new Error(FINAL_ALREADY_DECIDED);
    }

    await docs.markVersionTransmitted(survey.documentVersionId);

    const snapshot = buildSurveyDeltaSnapshot({
      firstSurveyId: first.id,
      finalSurveyId: survey.id,
      firstVersionId: acceptedFirstVersionId,
      finalVersionId: survey.documentVersionId,
      firstHeader: firstLoaded.header,
      finalHeader: finalLoaded.header,
      firstLines: firstLoaded.lines || [],
      finalLines: finalLoaded.lines || [],
      comparedAt: nowIso,
      classification: 'pending_review',
      comparedAgainst: 'first_survey',
      previousDeltaId: null,
    });

    const quote = await findProjectQuotation(survey.projectId);
    const { data: deltaRow, error: deltaErr } = await directSupabase
      .from('final_survey_deltas')
      .insert({
        project_id: survey.projectId,
        document_id: quote?.id || null,
        first_survey_id: first.id,
        final_survey_id: survey.id,
        first_version_id: acceptedFirstVersionId,
        final_version_id: survey.documentVersionId,
        snapshot_json: snapshot,
        classification: 'pending_review',
        completed_at: nowIso,
        completed_by: userId,
        decided_at: null,
        decided_by: null,
      })
      .select('*')
      .maybeSingle();
    if (deltaErr) throw new Error(deltaErr.message);
    if (!deltaRow?.id) throw new Error('Failed to store Final Survey delta.');

    const delta = fromDeltaRow(deltaRow);
    emitActivityEventBackground({
      action: 'survey.final_completed',
      entityType: 'survey',
      entityId: survey.id,
      projectId: survey.projectId,
      actorId: userId,
      label: 'Final Survey marked complete — pending reapproval review',
      metadata: { deltaId: delta.id, classification: 'pending_review', comparedAgainst: 'first_survey' },
    });

    return { survey: fromSurveyRow(updated), delta };
  },

  /**
   * Staff decision after Review Changes.
   * No → no_reapproval: Final operational + advance live customer document to Final snapshot (no email).
   * Yes → customer_reapproval_required (workspace then transmits immediately; operational after customer accept).
   */
  async decideFinalSurveyReapproval(
    deltaId: string,
    requiresCustomerReapproval: boolean,
  ): Promise<FinalSurveyDeltaRecord> {
    const existing = await this.getFinalSurveyDeltaById(deltaId);
    const finalSurvey = await this.getById(existing.finalSurveyId);
    const decideAction = resolveDecideFinalDeltaAction({ survey: finalSurvey, delta: existing });
    if (decideAction.type === 'reject') throw new Error(decideAction.reason);
    await assertProjectFinalSurveyMayChange(existing.projectId);

    const classification: SurveyDeltaClassification = requiresCustomerReapproval
      ? 'customer_reapproval_required'
      : 'no_reapproval';
    const { data: userRes } = await directSupabase.auth.getUser();
    const userId = userRes.user?.id ?? null;
    const nowIso = new Date().toISOString();
    const nextSnapshot: SurveyDeltaSnapshot = {
      ...existing.snapshot,
      classification,
    };

    let liveVersionId: string | null = null;
    if (!requiresCustomerReapproval) {
      if (!finalSurvey.documentVersionId) {
        throw new Error('Final Survey has no completed version to publish live.');
      }
      const quote = await findProjectQuotation(existing.projectId);
      if (!quote?.id) throw new Error('Workspace quote not found.');

      // Ensure Final working lines are projected before snapshotting live content.
      try {
        await this.projectToVersion(finalSurvey.id, finalSurvey.documentVersionId);
      } catch (err) {
        console.warn('Final projectToVersion before live update skipped/failed:', err);
      }

      const docs = await getDocumentsService();
      // New immutable snapshot for live pointer — never mutates First transmission pins.
      const liveSnapshot = await docs.createImmutableTransmission(finalSurvey.documentVersionId);
      liveVersionId = liveSnapshot.id;
      await setDocumentCustomerLiveVersion(String(quote.id), liveVersionId);
      // Staff current working view stays on completed Final working record.
      await docs.activateVersion(String(quote.id), finalSurvey.documentVersionId);
    }

    const updatePayload: Record<string, unknown> = {
      classification,
      snapshot_json: nextSnapshot,
      decided_at: nowIso,
      decided_by: userId,
    };
    if (liveVersionId) updatePayload.live_version_id = liveVersionId;

    const { data: updated, error } = await directSupabase
      .from('final_survey_deltas')
      .update(updatePayload)
      .eq('id', deltaId)
      .select('*')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!updated) throw new Error('Failed to save reapproval decision.');

    const delta = fromDeltaRow(updated);
    emitActivityEventBackground({
      action: requiresCustomerReapproval
        ? 'survey.final_reapproval_required'
        : 'survey.final_live_updated_without_reapproval',
      entityType: 'survey',
      entityId: existing.finalSurveyId,
      projectId: existing.projectId,
      actorId: userId,
      label: requiresCustomerReapproval
        ? 'Final Survey requires customer reapproval'
        : 'Live customer document updated to Final without reapproval',
      metadata: {
        deltaId: delta.id,
        classification,
        liveVersionId,
        decidedAt: nowIso,
        firstTransmissionPreserved: true,
      },
    });
    return delta;
  },

  /**
   * Transmit Final Survey for customer reapproval.
   * Creates an immutable transmission + public URL; does not make Final operational yet.
   */
  async transmitFinalSurveyForApproval(finalSurveyId: string): Promise<{
    delta: FinalSurveyDeltaRecord;
    shareUrl: string;
    shareLinkId: string;
    transmissionVersionId: string;
  }> {
    const survey = await this.getById(finalSurveyId);
    if (survey.kind !== 'final') throw new Error('Only Final Survey can be transmitted for reapproval.');
    await assertProjectFinalSurveyMayChange(survey.projectId);
    if (survey.completionStatus !== 'complete') {
      throw new Error('Mark Final Survey Complete and decide reapproval before transmitting.');
    }
    if (!survey.documentVersionId) {
      throw new Error('Final Survey has no working quote record to transmit.');
    }

    const delta = await this.getLatestFinalSurveyDelta(survey.id);
    if (!delta || delta.classification !== 'customer_reapproval_required') {
      throw new Error('Transmit for Approval is only available after staff chooses Yes on Review Changes.');
    }
    if (delta.customerAcceptedAt) {
      throw new Error('This Final Survey reapproval has already been accepted by the customer.');
    }

    if (delta.reapprovalShareLinkId) {
      try {
        await nullifyDocumentShareLink(
          delta.reapprovalShareLinkId,
          'Final Survey retransmitted — previous approval link withdrawn',
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err || '');
        if (!/already nullified/i.test(message)) throw err;
      }
    }

    const quote = await findProjectQuotation(survey.projectId);
    if (!quote?.id) throw new Error('Workspace quote not found.');

    // Ensure Final working lines are projected before snapshotting.
    try {
      await this.projectToVersion(survey.id, survey.documentVersionId);
    } catch (err) {
      // Working may be staff-locked; unlock is not required for read/project if override path fails.
      // Prefer projecting when writable; otherwise clone from current stored version lines.
      console.warn('Final projectToVersion before reapproval transmit skipped/failed:', err);
    }

    const { sendDocumentToCustomer } = await import('./sendDocumentToCustomer.service');
    const sendResult = await sendDocumentToCustomer(String(quote.id), {
      versionId: survey.documentVersionId,
      // Quote was already accepted on First Survey — keep accepted/invoiced/paid status.
      preserveDocumentStatus: true,
      surveyArtefact: 'final',
      allowLockedTransmit: true,
    });

    let shareLinkId = String(sendResult.shareLinkId || '').trim();
    let resolvedTransmissionId = String(sendResult.sentVersionId || '').trim();
    if (!resolvedTransmissionId && shareLinkId) {
      const { data: linkRow } = await directSupabase
        .from('document_share_links')
        .select('sent_version_id')
        .eq('id', shareLinkId)
        .maybeSingle();
      resolvedTransmissionId = String(linkRow?.sent_version_id || '').trim();
    }
    if (!resolvedTransmissionId) {
      throw new Error('Transmit succeeded but the Final transmission snapshot id was not recorded.');
    }

    const { data: updated, error } = await directSupabase
      .from('final_survey_deltas')
      .update({
        reapproval_transmission_version_id: resolvedTransmissionId,
        reapproval_share_link_id: shareLinkId || null,
      })
      .eq('id', delta.id)
      .select('*')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!updated) throw new Error('Failed to record Final reapproval transmission.');

    const next = fromDeltaRow(updated);
    emitActivityEventBackground({
      action: 'survey.final_reapproval_transmitted',
      entityType: 'survey',
      entityId: survey.id,
      projectId: survey.projectId,
      label: 'Final Survey transmitted for customer reapproval',
      metadata: {
        deltaId: next.id,
        transmissionVersionId: resolvedTransmissionId,
        shareLinkId: shareLinkId || null,
        shareUrl: sendResult.shareUrl,
      },
    });

    return {
      delta: next,
      shareUrl: sendResult.shareUrl,
      shareLinkId,
      transmissionVersionId: resolvedTransmissionId,
    };
  },

  async markFinalReapprovalAccepted(args: {
    transmissionVersionId: string;
    shareLinkId?: string | null;
  }): Promise<FinalSurveyDeltaRecord | null> {
    const transmissionVersionId = String(args.transmissionVersionId || '').trim();
    if (!transmissionVersionId) return null;

    const { data: row, error } = await directSupabase
      .from('final_survey_deltas')
      .select('*')
      .eq('reapproval_transmission_version_id', transmissionVersionId)
      .order('completed_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;

    const existing = fromDeltaRow(row);
    if (existing.supersededAt) {
      throw new Error(FINAL_REVIEW_SUPERSEDED);
    }
    if (existing.customerAcceptedAt) return existing;
    await assertProjectFinalSurveyMayChange(existing.projectId);

    const nowIso = new Date().toISOString();
    const { data: updated, error: upErr } = await directSupabase
      .from('final_survey_deltas')
      .update({
        customer_accepted_at: nowIso,
        customer_accepted_share_link_id: args.shareLinkId ? String(args.shareLinkId) : null,
        live_version_id: transmissionVersionId,
      })
      .eq('id', existing.id)
      .select('*')
      .maybeSingle();
    if (upErr) throw new Error(upErr.message);
    if (!updated) throw new Error('Failed to record Final reapproval acceptance.');

    if (existing.documentId) {
      await setDocumentCustomerLiveVersion(String(existing.documentId), transmissionVersionId);
    } else if (existing.projectId) {
      const quote = await findProjectQuotation(existing.projectId);
      if (quote?.id) await setDocumentCustomerLiveVersion(String(quote.id), transmissionVersionId);
    }

    const delta = fromDeltaRow(updated);
    emitActivityEventBackground({
      action: 'survey.final_reapproval_accepted',
      entityType: 'survey',
      entityId: existing.finalSurveyId,
      projectId: existing.projectId,
      label: 'Customer accepted Final Survey reapproval',
      metadata: {
        deltaId: delta.id,
        transmissionVersionId,
        shareLinkId: args.shareLinkId || null,
        liveVersionId: transmissionVersionId,
      },
    });
    return delta;
  },

  /**
   * Reopen a completed Final Survey for another controlled working cycle.
   * Preserves prior completed history and abandons any unresolved pending review.
   * The next Mark Complete always compares against the original First transmission.
   */
  async reopenFinalSurvey(surveyId: string): Promise<VisualDesignerSurvey> {
    const survey = await this.getById(surveyId);
    if (survey.kind !== 'final') throw new Error('Only Final Survey can be reopened.');
    await assertProjectFinalSurveyMayChange(survey.projectId);
    const currentDelta = await fetchCurrentFinalSurveyDelta(survey.id);
    const frozen = survey.completionStatus === 'complete' || isUnresolvedPendingReviewDelta(currentDelta);
    if (!frozen) return survey;
    if (!survey.documentVersionId) throw new Error('Final Survey has no completed version to reopen from.');

    const docs = await getDocumentsService();
    const quote = await findProjectQuotation(survey.projectId);
    if (!quote?.id) throw new Error('Workspace quote not found.');

    const { data: userRes } = await directSupabase.auth.getUser();
    const userId = userRes.user?.id ?? null;
    const nowIso = new Date().toISOString();

    const withdrawInFlight = isInFlightFinalReapproval(currentDelta);
    const { error: supersedeErr } = await directSupabase
      .from('final_survey_deltas')
      .update({
        superseded_at: nowIso,
        superseded_by: userId,
      })
      .eq('final_survey_id', survey.id)
      .is('superseded_at', null)
      .is('customer_accepted_at', null);
    if (supersedeErr) {
      if (/customer_accepted_at/i.test(supersedeErr.message)) {
        const fallback = await directSupabase
          .from('final_survey_deltas')
          .update({
            superseded_at: nowIso,
            superseded_by: userId,
          })
          .eq('final_survey_id', survey.id)
          .is('superseded_at', null)
          .is('decided_at', null);
        if (fallback.error && !/superseded_at/i.test(fallback.error.message)) {
          throw new Error(fallback.error.message);
        }
        if (shouldSupersedeDeltaOnFinalReopen(currentDelta) && currentDelta?.id) {
          const byId = await directSupabase
            .from('final_survey_deltas')
            .update({
              superseded_at: nowIso,
              superseded_by: userId,
            })
            .eq('id', currentDelta.id)
            .is('superseded_at', null);
          if (byId.error && !/superseded_at/i.test(byId.error.message)) {
            throw new Error(byId.error.message);
          }
        }
      } else if (!/superseded_at/i.test(supersedeErr.message)) {
        throw new Error(supersedeErr.message);
      }
    }

    const pendingShareId = String(currentDelta?.reapprovalShareLinkId || '').trim();
    if (pendingShareId && shouldSupersedeDeltaOnFinalReopen(currentDelta)) {
      try {
        await nullifyDocumentShareLink(
          pendingShareId,
          'Final Survey reopened — in-flight approval withdrawn',
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err || '');
        if (!/already nullified/i.test(message)) throw err;
      }
    }

    const completedVersionId = String(survey.documentVersionId);
    const reuseInPlace = !(await finalRecordIsReferencedElsewhere({
      versionId: completedVersionId,
      projectId: survey.projectId,
    }));
    const workingVersionId = reuseInPlace
      ? completedVersionId
      : String((await docs.cloneVersionFrom(String(quote.id), completedVersionId)).id);
    const { error: unlockErr } = await directSupabase
      .from('document_versions')
      .update({
        is_transmitted: false,
        override_unlocked: false,
        transmitted_at: null,
        transmitted_by: null,
      })
      .eq('id', workingVersionId);
    if (unlockErr) throw new Error(unlockErr.message);

    const { data: updated, error } = await directSupabase
      .from('visual_designer_surveys')
      .update({
        completion_status: 'in_progress',
        completed_at: null,
        completed_by: null,
        reopened_at: nowIso,
        reopened_by: userId,
        work_started_at: nowIso,
        work_started_by: userId,
      })
      .eq('id', survey.id)
      .select('*')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!updated) throw new Error('Failed to reopen Final Survey.');

    if (!reuseInPlace) await this.rebindToVersion(survey.id, workingVersionId);
    await this.projectToVersion(survey.id, workingVersionId);
    await docs.activateVersion(String(quote.id), workingVersionId);

    const row = await this.getById(survey.id);
    emitActivityEventBackground({
      action: 'survey.final_reopened',
      entityType: 'survey',
      entityId: survey.id,
      projectId: survey.projectId,
      actorId: userId,
      label: 'Final Survey reopened for further edits',
      metadata: {
        newVersionId: workingVersionId,
        reopenedInPlace: reuseInPlace,
        priorCompletedVersionPreserved: !reuseInPlace,
        supersededPendingReview: true,
        withdrawnInFlightReapproval: withdrawInFlight,
        comparedAgainstNextComplete: 'first_survey',
      },
    });
    return row;
  },

  async renameSurvey(surveyId: string, label: string): Promise<VisualDesignerSurvey> {
    const trimmed = label.trim();
    if (!trimmed) throw new Error('Name is required');
    const { data, error } = await directSupabase
      .from('visual_designer_surveys')
      .update({ label: trimmed })
      .eq('id', surveyId)
      .eq('kind', 'intermediate')
      .select('*')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error('First Survey and Final Survey cannot be renamed.');
    return fromSurveyRow(data);
  },

  canRenameSurvey(survey: Pick<VisualDesignerSurvey, 'kind'> | null): { ok: boolean; reason?: string } {
    if (!survey) return { ok: false, reason: 'No survey selected' };
    if (survey.kind === 'first') return { ok: false, reason: 'First Survey cannot be renamed.' };
    if (survey.kind === 'final') return { ok: false, reason: 'Final Survey cannot be renamed.' };
    return { ok: true };
  },

  canDeleteSurvey(
    survey: VisualDesignerSurvey | null,
    versionLock?: { isTransmitted: boolean; overrideUnlocked: boolean } | null,
    options?: { firstHasBeenTransmitted?: boolean },
  ): { ok: boolean; reason?: string } {
    if (!survey) return { ok: false, reason: 'No survey selected' };
    const transmitted = !!(
      options?.firstHasBeenTransmitted
      || (survey.kind === 'first' && versionLock?.isTransmitted)
    );
    if ((survey.kind === 'first' || survey.kind === 'final') && transmitted) {
      return { ok: false, reason: SURVEY_IMMUTABLE_AFTER_FIRST_TRANSMIT };
    }
    if (survey.documentVersionId && versionLock?.isTransmitted && !versionLock.overrideUnlocked) {
      return { ok: false, reason: 'This survey is locked with a transmitted quote. Unlock override, or keep it as the historical record.' };
    }
    return { ok: true };
  },

  /**
   * Physically delete First/Final Survey (+ items/deltas).
   * - First: new empty survey + brand-new blank quote version (create-time defaults).
   *   Blocked while a Final Survey still exists — delete Final first.
   * - Final: delete only (no immediate re-clone). Final is re-provisioned on the next
   *   First transmit (or legacy Create Final / accept ensure).
   * First and Final are separate rows/items/versions — deleting Final never consumes First.
   * Transmitted versions are left as historical records (unbound only).
   */
  async resetSurveySheet(surveyId: string): Promise<VisualDesignerSurvey | null> {
    const survey = await this.getById(surveyId);
    if (survey.kind !== 'first' && survey.kind !== 'final') {
      throw new Error('Only First Survey and Final Survey sheets can be cleared.');
    }
    const projectId = survey.projectId;
    const kind = survey.kind;
    const sortOrder = survey.sortOrder;
    const oldVersionId = survey.documentVersionId ? String(survey.documentVersionId) : '';
    const previousSurveyId = survey.id;
    const docs = await getDocumentsService();
    const quote = await findProjectQuotation(projectId);
    if (!quote?.id) throw new Error('Create a quote or estimate before clearing this survey.');

    if (await projectFirstSurveyIsImmutable(projectId)) {
      throw new Error(SURVEY_IMMUTABLE_AFTER_FIRST_TRANSMIT);
    }

    let oldWasActive = false;
    if (oldVersionId) {
      const { data: ver, error: verErr } = await directSupabase
        .from('document_versions')
        .select('id, is_active, is_transmitted, override_unlocked')
        .eq('id', oldVersionId)
        .maybeSingle();
      if (verErr) throw new Error(verErr.message);
      if (ver) {
        oldWasActive = !!ver.is_active;
        await directSupabase
          .from('document_versions')
          .update({ visual_survey_id: null })
          .eq('id', oldVersionId)
          .eq('visual_survey_id', survey.id);
      }
    }

    const { error: delErr } = await directSupabase
      .from('visual_designer_surveys')
      .delete()
      .eq('id', survey.id);
    if (delErr) throw new Error(delErr.message);

    // Final Survey: remove only. Re-provision happens on Accept/Cash (or backfill), not here.
    if (kind === 'final') {
      emitActivityEventBackground({
        action: 'survey.cleared',
        entityType: 'survey',
        entityId: previousSurveyId,
        projectId,
        label: 'Final Survey cleared',
        metadata: {
          previousSurveyId,
          previousVersionId: oldVersionId || null,
          kind: 'final',
          reprovisioned: false,
        },
      });
      return null;
    }

    // First Survey: brand-new empty sheet + create-time quote version defaults (no residue).
    const { data: createdRow, error: createErr } = await directSupabase
      .from('visual_designer_surveys')
      .insert({
        project_id: projectId,
        kind: 'first',
        label: 'First Survey',
        sort_order: sortOrder,
        completion_status: 'in_progress',
        completed_at: null,
        completed_by: null,
        reopened_at: null,
        reopened_by: null,
      })
      .select('*')
      .maybeSingle();
    if (createErr) throw new Error(createErr.message);
    if (!createdRow) throw new Error('Failed to recreate First Survey.');
    const created = fromSurveyRow(createdRow);

    const { data: latest, error: latestErr } = await directSupabase
      .from('document_versions')
      .select('version_number')
      .eq('document_id', quote.id)
      .order('version_number', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (latestErr) throw new Error(latestErr.message);
    const nextNumber = (latest?.version_number ? Number(latest.version_number) : 0) + 1;

    // Match createDraftQuote / wizard create defaults — not a copy of the deleted sheet.
    const deposit = defaultDepositTerms(await isWorkspaceSupplyOnly(projectId));
    const { data: inserted, error: insErr } = await directSupabase
      .from('document_versions')
      .insert({
        document_id: quote.id,
        version_number: nextNumber,
        is_active: false,
        title: null,
        description: null,
        admin_pct: 0.10,
        admin_cap: 200,
        contingency_pct: 0.10,
        profit_pct: 0.10,
        vat_enabled: true,
        vat_pct: 0.20,
        input_vat_selected: false,
        deposit_amount: deposit.depositAmount,
        deposit_percentage: deposit.depositPercentage,
        has_deposit: deposit.hasDeposit,
        project_days: null,
        project_hours: null,
        payment_structure_applicable: false,
        payment_structure_pattern: null,
        is_transmitted: false,
        override_unlocked: false,
        visual_survey_id: null,
      })
      .select('id')
      .maybeSingle();
    if (insErr) throw new Error(insErr.message);
    if (!inserted?.id) throw new Error('Failed to create a fresh quote version for First Survey.');
    const targetVersionId = String(inserted.id);

    await this.bindToVersion(created.id, targetVersionId);
    try {
      await docs.activateVersion(String(quote.id), targetVersionId);
    } catch (err) {
      console.warn('Survey reset: could not activate fresh First Survey version', err);
      if (oldWasActive) {
        // Best-effort: leave the new version bound even if activation failed.
      }
    }

    // Keep document header deposit/fees aligned with the fresh working version.
    try {
      await docs.updateDocument(String(quote.id), {
        ...deposit,
        fees: { adminPct: 0.10, adminCap: 200, contingencyPct: 0.10, profitPct: 0.10, developerPct: null },
        vatEnabled: true,
        vatPct: 0.20,
        inputVatSelected: false,
        description: '',
      });
    } catch (err) {
      console.warn('Survey reset: could not reset document header defaults', err);
    }

    emitActivityEventBackground({
      action: 'survey.cleared',
      entityType: 'survey',
      entityId: created.id,
      projectId,
      label: 'First Survey cleared',
      metadata: {
        previousSurveyId,
        previousVersionId: oldVersionId || null,
        newVersionId: targetVersionId,
        kind: 'first',
        reprovisioned: true,
      },
    });

    return this.getById(created.id);
  },

  async deleteSurvey(survey: VisualDesignerSurvey): Promise<void> {
    const surveyId = String(survey?.id || '').trim();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(surveyId)) {
      throw new Error('Cannot delete survey: missing id.');
    }
    let lock: { isTransmitted: boolean; overrideUnlocked: boolean } | null = null;
    if (survey.documentVersionId) {
      try {
        lock = await getVersionLock(survey.documentVersionId);
      } catch {
        lock = null;
      }
    }
    const firstHasBeenTransmitted = await projectFirstSurveyIsImmutable(survey.projectId);
    const allowed = this.canDeleteSurvey({ ...survey, id: surveyId }, lock, { firstHasBeenTransmitted });
    if (!allowed.ok) throw new Error(allowed.reason || 'This survey cannot be deleted.');
    const { error } = await directSupabase
      .from('visual_designer_surveys')
      .delete()
      .eq('id', surveyId);
    if (error) throw new Error(error.message);
  },

  /**
   * Force-clear First / Final / intermediate surveys for one workspace only.
   * Does not touch other projects, documents, or legacy version histories on other quotes.
   */
  async resetAllForProject(projectId: string): Promise<void> {
    const id = String(projectId || '').trim();
    if (!id) throw new Error('Missing project id');
    if (await projectFirstSurveyIsImmutable(id)) {
      throw new Error(SURVEY_IMMUTABLE_AFTER_FIRST_TRANSMIT);
    }
    const { error } = await directSupabase
      .from('visual_designer_surveys')
      .delete()
      .eq('project_id', id);
    if (error) throw new Error(error.message);
  },

  /**
   * If First/Final rows were wiped after transmission, restore the historical First bind
   * to the original working version and provision Final when missing.
   */
  async repairTransmittedSurveyLifecycle(projectId: string): Promise<boolean> {
    const id = String(projectId || '').trim();
    if (!id) return false;
    if (!(await projectFirstSurveyIsImmutable(id))) return false;

    const quote = await findProjectQuotation(id);
    if (!quote?.id) return false;

    const { data: firstLinks, error: linkErr } = await directSupabase
      .from('document_share_links')
      .select('source_version_id, sent_version_id, created_at')
      .eq('document_id', quote.id)
      .eq('survey_artefact', 'first')
      .order('created_at', { ascending: true })
      .limit(1);
    if (linkErr && !/survey_artefact|schema cache/i.test(String(linkErr.message || ''))) {
      throw new Error(linkErr.message);
    }
    const firstLink = (firstLinks || [])[0];
    const originalWorkingId = String(firstLink?.source_version_id || '').trim();
    const originalSentId = String(firstLink?.sent_version_id || '').trim();

    const { data: transmitted, error: txErr } = await directSupabase
      .from('document_versions')
      .select('id, is_transmitted')
      .eq('document_id', quote.id)
      .eq('is_transmitted', true)
      // Newest first: a First re-sent before acceptance replaces the earlier send.
      .order('version_number', { ascending: false });
    if (txErr) throw new Error(txErr.message);
    const fallbackWorkingId = String((transmitted || [])[0]?.id || '').trim();
    const bindTo = originalWorkingId || fallbackWorkingId || originalSentId;
    if (!bindTo) {
      const surveys = await this.listByProject(id);
      if (!surveys.some((row) => row.kind === 'final')) {
        await this.createFinalSurveyFromFirst(id);
        return true;
      }
      return false;
    }

    let first = (await this.listByProject(id)).find((row) => row.kind === 'first' && !row.optionId)
      || (await this.listByProject(id)).find((row) => row.kind === 'first')
      || await this.ensureFirstSurvey(id);
    const currentBind = String(first.documentVersionId || '').trim();
    let changed = false;
    if (currentBind !== bindTo) {
      const currentLock = currentBind ? await getVersionLock(currentBind).catch(() => null) : null;
      if (!currentLock?.isTransmitted) {
        await this.rebindToVersion(first.id, bindTo).catch(async () => {
          await this.bindToVersion(first.id, bindTo);
        });
        changed = true;
        first = await this.getById(first.id);
      }
    }
    if (first.documentVersionId && await seedEmptySheetFromRecord(first.id, String(first.documentVersionId)) > 0) {
      changed = true;
    }

    const surveys = await this.listByProject(id);
    if (!surveys.some((row) => row.kind === 'final')) {
      // Clone what First actually shows; it may have kept a later send than bindTo.
      await this.createFinalSurveyFromFirst(id, {
        fromTransmissionVersionId: originalSentId || String(first.documentVersionId || '').trim() || bindTo,
      });
      changed = true;
    }
    return changed;
  },

  async cloneSurveyForVersion(
    sourceSurveyId: string,
    versionId: string,
    label: string,
    kind: VisualDesignerSurveyKind = 'intermediate'
  ): Promise<VisualDesignerSurvey> {
    const source = await this.getById(sourceSurveyId);
    const items = await this.listItems(sourceSurveyId);
    const surveys = await this.listByProject(source.projectId);
    const nextOrder = surveys.reduce((max, s) => Math.max(max, s.sortOrder), 0) + 1;
    const { data, error } = await directSupabase
      .from('visual_designer_surveys')
      .insert({
        project_id: source.projectId,
        kind,
        label,
        sort_order: nextOrder,
        cloned_from_survey_id: source.id,
        document_version_id: versionId,
      })
      .select('*')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error('Failed to clone survey');
    const created = fromSurveyRow(data);
    const clonedItems = items.filter(isVisualDesignerLine).map(cloneLineForBoundVersion);
    if (clonedItems.length > 0) {
      const payload = clonedItems.map((line, idx) => lineToItemPayload(created.id, line, idx));
      const { error: insErr } = await directSupabase.from('visual_designer_survey_items').insert(payload);
      if (insErr) throw new Error(insErr.message);
    }
    const { error: dErr } = await directSupabase
      .from('document_versions')
      .update({ visual_survey_id: created.id })
      .eq('id', versionId);
    if (dErr) throw new Error(dErr.message);
    await this.projectToVersion(created.id, versionId);
    return created;
  },

  async backfillFromVersionLines(
    projectId: string,
    versionId: string,
    versionNumber: number,
    lines: DocumentLineInput[]
  ): Promise<VisualDesignerSurvey | null> {
    const vdLines = (lines || []).filter(isVisualDesignerLine);
    if (vdLines.length === 0) return null;
    const surveys = await this.listByProject(projectId);
    const already = surveys.find((s) => s.documentVersionId === versionId);
    if (already) return already;
    const hasFirst = surveys.some((s) => s.kind === 'first');
    const kind: VisualDesignerSurveyKind = hasFirst ? 'intermediate' : 'first';
    const label = kind === 'first' ? 'First Survey' : `Quote version ${versionNumber}`;
    const nextOrder = surveys.reduce((max, s) => Math.max(max, s.sortOrder), 0) + (surveys.length ? 1 : 0);
    const { data, error } = await directSupabase
      .from('visual_designer_surveys')
      .insert({
        project_id: projectId,
        kind,
        label,
        sort_order: nextOrder,
        document_version_id: versionId,
      })
      .select('*')
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error('Failed to backfill survey');
    const created = fromSurveyRow(data);
    const payload = vdLines.map((line, idx) => lineToItemPayload(created.id, cloneLineForBoundVersion(line), idx));
    const { error: insErr } = await directSupabase.from('visual_designer_survey_items').insert(payload);
    if (insErr) throw new Error(insErr.message);
    await directSupabase
      .from('document_versions')
      .update({ visual_survey_id: created.id })
      .eq('id', versionId);
    return created;
  },

  attachableSurveys(surveys: VisualDesignerSurvey[], currentVersionId?: string | null): VisualDesignerSurvey[] {
    return surveys.filter((s) => !s.documentVersionId || s.documentVersionId === currentVersionId);
  },
};
